/* eslint-disable max-lines */
import * as React from 'react';
import {
  ArrowPathIcon,
  CameraIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import {
  Button,
  Card,
  Container,
  Dropdown,
  Grid,
  Modal,
  NormalColors,
  Pagination,
  Row,
  Spacer,
  Text,
  User,
  useTheme,
} from '@nextui-org/react';
import produce from 'immer';

import { abbreviateNumber } from 'js-abbreviation-number';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { ROUTES } from '@src/config/constants';
import { FEED_COUNT } from '@src/config/settings';
import {
  GetAssetsDocument,
  StatusType,
  useGetAssetsCountQuery,
  useGetAssetsQuery,
  useGetHandlesForClientsQuery,
  useSetAssetApprovedMutation,
  useSetAssetStatusMutation,
  useSetAssetTagsMutation,
} from '@src/generated/hooks';
import { AppFilterableListItem } from '@src/layouts/app/AppFilterableList';
import { AppFullWidthContainer } from '@src/layouts/app/AppFullWidthContainer';
import { AppFullWidthLoader } from '@src/layouts/app/AppFullWidthLoader';
import { AppLoadingButton } from '@src/layouts/app/AppLoadingButton';
import { AppTitle } from '@src/layouts/app/AppTitle';
import { AssetCardFooter } from '@src/layouts/feed/AssetCardFooter';
import { AssetCardImage } from '@src/layouts/feed/AssetCardImage';
import { ModalTagSelect } from '@src/layouts/feed/ModalTagSelect';
import { buildAssetWhereClause, getAllHandleIds } from '@src/layouts/feed/util';
import { decodeQueryParams, encodeQueryParams } from '@src/util/navigation';

interface AssetCardListProps {}

/**
 * @name AssetCardList
 * @description tbd...
 */
export function AssetCardList(_props: AssetCardListProps) {
  // const {} = props

  // Hooks
  const router = useRouter();
  const { theme } = useTheme();

  const [deletingTags, setDeletingTags] = React.useState<
    { assetId: string; tagIds: string[] } | undefined
  >();
  const [selectedAssets, setSelectedAssets] = React.useState<string[]>([]);
  const [isTagSelectOpen, setIsTagSelectOpen] = React.useState(false);
  const [isFullScreenLoading, setIsFullScreenLoading] = React.useState(false);

  // Setup
  const { query } = router;
  const decodedQuery = decodeQueryParams(query);

  // GraphQL
  const {
    data: igHandlesData,
    loading: igHandlesLoading,
    refetch: refetchHandles,
  } = useGetHandlesForClientsQuery({
    variables: {
      clientNames: decodedQuery.clients ?? [],
    },
  });

  const allHandleIds = getAllHandleIds(igHandlesData);
  const where = buildAssetWhereClause(query, allHandleIds);
  const {
    data: totalAssetsData,
    loading: totalAssetsLoading,
    refetch: refetchTotal,
  } = useGetAssetsCountQuery({
    skip: igHandlesLoading,
    variables: {
      where,
    },
  });

  const totalAssets = totalAssetsData?.igAssets.length ?? 0;
  const totalPages = Math.ceil(totalAssets / FEED_COUNT);
  const currentPage = Math.min(decodedQuery.page ?? 1, totalPages);
  const skip = (currentPage - 1) * FEED_COUNT;

  const {
    data,
    error,
    loading: getAssetsLoading,
    refetch,
  } = useGetAssetsQuery({
    skip:
      totalAssetsLoading ||
      !totalAssetsData ||
      !totalAssetsData.igAssets.length,
    variables: {
      skip,
      take: FEED_COUNT,
      where,
    },
  });

  // More setup
  const assets = data?.igAssets;
  const loading = getAssetsLoading || totalAssetsLoading || igHandlesLoading;

  const [setAssetTags] = useSetAssetTagsMutation({
    refetchQueries: [{ query: GetAssetsDocument }],
  });
  const [setAssetStatus] = useSetAssetStatusMutation({});
  const [setAssetApproved] = useSetAssetApprovedMutation({});

  // Handlers
  const onTagPress = async (assetId: string, tagIds: { id: string }[]) => {
    setDeletingTags({ assetId, tagIds: tagIds.map((tag) => tag.id) });

    await setAssetTags({
      variables: {
        id: assetId,
        tags: tagIds,
      },
    });

    setDeletingTags(undefined);
  };

  const onCardClick = (assetId: string) => {
    const newSelectedAssets = selectedAssets.includes(assetId)
      ? selectedAssets.filter((id) => id !== assetId)
      : [...selectedAssets, assetId];

    setSelectedAssets(newSelectedAssets);
  };

  const refetchAll = async () => {
    await refetchHandles();
    await refetchTotal();
    await refetch();
  };

  const onTagSelect = async (tags?: AppFilterableListItem[]) => {
    // Close the modal
    setIsTagSelectOpen(false);

    if (tags) {
      setIsFullScreenLoading(true);

      // Send requests to set the tags for all selected assets.
      const promises = selectedAssets.map((assetId) => {
        return setAssetApproved({
          variables: {
            id: assetId,
            tags: tags.map(({ id }) => ({ id })),
          },
        });
      });

      await Promise.all(promises);

      await refetchAll();

      // Close the modal and clear the selected assets.
      setIsFullScreenLoading(false);
      setSelectedAssets([]);
    }
  };

  const onSetStatusForSelected = async (status: StatusType) => {
    setIsFullScreenLoading(true);

    const promises = selectedAssets.map((assetId) => {
      setAssetStatus({
        variables: {
          id: assetId,
          status,
        },
      });
    });

    await Promise.all(promises);

    await refetchAll();

    // Close the modal and clear the selected assets.
    setIsFullScreenLoading(false);
    setSelectedAssets([]);
  };

  const onSelectAction = (key: React.Key) => {
    switch (key) {
      case 'approve':
        setIsTagSelectOpen(true);
        break;
      case 'reject':
        onSetStatusForSelected(StatusType.Rejected);
        break;
      case 'unset':
        onSetStatusForSelected(StatusType.Unset);
        break;
    }
  };

  const onSelectAll = () => {
    setSelectedAssets(assets?.map((asset) => asset.id) ?? []);
  };

  const onClearSelection = () => {
    setSelectedAssets([]);
  };

  return (
    <>
      <AppFullWidthContainer>
        <Spacer />
        <Row justify="flex-start" align="center">
          <AppTitle color="gray">
            <CameraIcon width={30} height={30} />
            Posts
          </AppTitle>
          <Spacer x={1} />
          <Button
            auto
            flat
            color={theme?.colors.neutral.value as NormalColors}
            onPress={() => refetchAll()}
            icon={<ArrowPathIcon width={20} height={20} />}
          />
        </Row>
        <Spacer y={0.5} />
        <Row>
          <Dropdown>
            <Dropdown.Button flat>
              {selectedAssets.length} selected
            </Dropdown.Button>
            <Dropdown.Menu
              variant="light"
              aria-label="Actions"
              onAction={onSelectAction}
            >
              <Dropdown.Item key="approve" color="success">
                Approve
              </Dropdown.Item>
              <Dropdown.Item key="reject" color="error">
                Reject
              </Dropdown.Item>
              <Dropdown.Item key="unset" withDivider>
                Unset
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Spacer x={1} />
          <Button auto flat color="secondary" onPress={onSelectAll}>
            Select All
          </Button>
          <Spacer x={0.5} />
          <Button
            auto
            flat
            color={theme?.colors.neutral.value as NormalColors}
            onPress={onClearSelection}
          >
            Clear
          </Button>
        </Row>
        <Spacer y={0.5} />
        {loading && <AppFullWidthLoader />}
        {!loading && !assets?.length && (
          <AppFullWidthContainer>
            <Text h3>No stories found that match the filters.</Text>
          </AppFullWidthContainer>
        )}
        {!loading && assets?.length && (
          <Grid.Container gap={1} css={{ p: 0 }}>
            {assets?.map((asset) => {
              const handleName = asset.igHandle.handle;
              const handleInitial = handleName[0].toUpperCase();
              const followers = asset.igHandle.followers;
              const followersAbbreviated = abbreviateNumber(followers);
              const tags = asset.tag;
              const isSelected = selectedAssets.includes(asset.id);

              const createdAt = DateTime.fromISO(
                asset.createdAt,
              ).toLocaleString({
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                month: 'short',
                weekday: 'short',
                year: 'numeric',
              });

              return (
                <Grid key={asset.id} xs={12} sm={4} md={3}>
                  <Card
                    css={{
                      borderColor: isSelected ? '$primary' : '',
                      w: '100%',
                    }}
                    variant="bordered"
                    onPress={() => onCardClick(asset.id)}
                    isPressable={true}
                    allowTextSelectionOnPress={false}
                    borderWeight={isSelected ? 'extrabold' : 'light'}
                  >
                    <Card.Header>
                      <AppFullWidthContainer>
                        <Row css={{ px: 0 }}>
                          <User
                            name={handleName}
                            text={handleInitial}
                            squared
                            description={`${followersAbbreviated} followers`}
                            css={{ px: 0 }}
                          />
                        </Row>
                        <Row css={{ px: '$2', py: '$5' }}>
                          <Text small>{createdAt}</Text>
                        </Row>
                      </AppFullWidthContainer>
                    </Card.Header>
                    <Card.Body css={{ pb: '$5', pt: '$1' }}>
                      <AppFullWidthContainer>
                        <Row>
                          <Grid.Container gap={0.5}>
                            {tags?.map((tag, index) => {
                              return (
                                <Grid key={`asset#${asset.id}tag#${index}`}>
                                  <AppLoadingButton
                                    size="xs"
                                    auto
                                    flat
                                    color="primary"
                                    loading={
                                      deletingTags?.assetId === asset.id &&
                                      !deletingTags?.tagIds.includes(tag.id)
                                    }
                                    iconRight={
                                      <XMarkIcon width={20} height={20} />
                                    }
                                    onPress={() =>
                                      onTagPress(
                                        asset.id,
                                        tags
                                          .filter((t) => t.id !== tag.id)
                                          .map(({ id }) => ({
                                            id,
                                          })),
                                      )
                                    }
                                  >
                                    {tag.name}
                                  </AppLoadingButton>
                                </Grid>
                              );
                            })}
                          </Grid.Container>
                        </Row>
                        <Spacer y={0.25} />
                        <Row>
                          <AssetCardImage asset={asset} />
                        </Row>
                      </AppFullWidthContainer>
                    </Card.Body>
                    <Card.Divider />
                    <Card.Footer>
                      <AssetCardFooter asset={asset} />
                    </Card.Footer>
                  </Card>
                </Grid>
              );
            })}
          </Grid.Container>
        )}
        {!loading && error && (
          <Container>
            <Spacer y={3} />
            <Row justify="center">
              <Card css={{ mw: '400px' }}>
                <Card.Body>
                  <Text color="error">
                    There was an error loading the feed. Try refreshing the
                    page.
                  </Text>
                </Card.Body>
              </Card>
            </Row>
          </Container>
        )}
        <Spacer />

        {!loading && (
          <Row justify="center">
            <Pagination
              total={totalPages}
              page={currentPage}
              onChange={(page) => {
                const newQuery = produce(query, (draft) => {
                  draft.page = page.toString();
                });

                router.push({
                  pathname: ROUTES.feed,
                  query: encodeQueryParams(newQuery),
                });
              }}
            />
          </Row>
        )}
      </AppFullWidthContainer>
      {isTagSelectOpen && (
        <ModalTagSelect
          visible={isTagSelectOpen}
          onOk={onTagSelect}
          onCancel={() => setIsTagSelectOpen(false)}
          selected={[]}
        />
      )}
      {
        <Modal blur={true} open={isFullScreenLoading}>
          <Modal.Header>
            <Text id="modal-title" size={18}>
              Processing
            </Text>
          </Modal.Header>
          <Modal.Body>
            <AppFullWidthLoader />
          </Modal.Body>
          <Modal.Footer></Modal.Footer>
        </Modal>
      }
    </>
  );
}
